import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { clearImpersonation, getImpersonation, parseImpersonationToken, startImpersonation } from './impersonation';

/**
 * Landing route for the ops console: /impersonate#token=<jwt>. The token travels
 * in the URL fragment (never sent to the server, never logged), is stored for
 * this tab only, and the hash is wiped before the app loads.
 * /impersonate#ended is where an impersonation tab lands after "Exit".
 */
const ImpersonateEntry: React.FC = () => {
    const { t } = useTranslation();
    const [state, setState] = useState<'pending' | 'ok' | 'invalid' | 'ended'>('pending');

    const location = useLocation();

    // Re-runs on every hash change: the route may already be mounted (e.g. on the
    // "#ended" page) when the console opens a new "#token=" link in the same tab.
    useEffect(() => {
        const hash = location.hash.replace(/^#/, '');
        if (hash === 'ended') {
            clearImpersonation();
            setState('ended');
            return;
        }
        const token = new URLSearchParams(hash).get('token') || '';
        if (token) {
            const imp = parseImpersonationToken(token);
            // Wipe the token from the URL and from this tab's history either way.
            window.history.replaceState(null, '', window.location.pathname);
            if (!imp) {
                setState('invalid');
                return;
            }
            startImpersonation(imp);
            setState('ok');
            return;
        }
        setState(getImpersonation() ? 'ok' : 'invalid');
    }, [location.hash]);

    if (state === 'ok') return <Navigate to="/home" replace />;
    if (state === 'pending') return <div data-cy="impersonate-pending">{t('common.loading')}</div>;
    return (
        <div className="gs-impersonation-end" data-cy={`impersonate-${state}`}>
            <h2>{state === 'ended' ? t('impersonation.endedTitle') : t('impersonation.invalidTitle')}</h2>
            <p>{state === 'ended' ? t('impersonation.endedText') : t('impersonation.invalidText')}</p>
        </div>
    );
};

export default ImpersonateEntry;
